// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, BackgroundImage } from '@mantine/core';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { Stack, Center, Container } from '@/components/ui/layout';
import { Title, Text, Button, Badge } from '@/components/ui/core';
import { PasswordInput } from '@/components/ui/inputs';
import { IconLock, IconArrowLeft, IconLockCheck, IconPhoto, IconCheck } from '@tabler/icons-react';
import { FadeIn, SlideIn } from '@/components/animations';
import { AuthIllustration, PasswordStrength } from '../components';

export default function ResetIllustrated() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isReset, setIsReset] = useState(false);
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsReset(true);
  };

  const passwordsMatch = password === confirmPassword;
  const canSubmit = password.length >= 8 && passwordsMatch;

  return (
    <Box style={{ minHeight: '100vh', display: 'flex' }}>
      {/* Left side - Illustration */}
      <Box
        style={{
          width: '50%',
          display: 'none',
          position: 'relative',
          overflow: 'hidden',
        }}
        visibleFrom="md"
      >
        <BackgroundImage
          src="https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?w=1200&q=80"
          style={{
            position: 'absolute',
            inset: 0,
          }}
        />
        <Box
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${theme.colors.green[9]}dd, ${theme.colors.teal[9]}cc)`,
          }}
        />
        <Center h="100%" p="xl" style={{ position: 'relative', zIndex: 1 }}>
          <Stack align="center" gap="xl">
            <SlideIn direction="down" delay={200}>
              <AuthIllustration type="reset-password" size={250} animated />
            </SlideIn>
            <FadeIn delay={400}>
              <Stack align="center" gap="sm">
                <Title order={2} c="white" ta="center">
                  Secure Password Reset
                </Title>
                <Text c="white" ta="center" opacity={0.9} maw={350}>
                  Create a strong, unique password to protect your account.
                </Text>
              </Stack>
            </FadeIn>
          </Stack>
        </Center>
      </Box>

      {/* Right side - Form */}
      <Box
        style={{
          flex: 1,
          backgroundColor: isDark ? theme.colors.dark[7] : theme.colors.white,
        }}
      >
        <Center mih="100vh" p="xl">
          <Container size="xs" w="100%">
            {isReset ? (
              <Stack gap="lg" align="center">
                <Center
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 20,
                    backgroundColor: 'var(--mantine-color-green-light)',
                  }}
                >
                  <IconCheck size={40} color="var(--mantine-color-green-6)" />
                </Center>

                <Stack align="center" gap="xs">
                  <Title order={2} ta="center">Password Reset!</Title>
                  <Text c="dimmed" ta="center" maw={300}>
                    Your password has been successfully reset.
                  </Text>
                </Stack>

                <Button
                  component={Link}
                  to="/app/demo/auth-pages/login/illustrated"
                  fullWidth
                  size="md"
                  radius="md"
                  variant="gradient"
                  gradient={{ from: 'green', to: 'teal' }}
                >
                  Continue to login
                </Button>
              </Stack>
            ) : (
              <Stack gap="lg">
                <Button
                  component={Link}
                  to="/app/demo/auth-pages/reset-password"
                  variant="subtle"
                  size="xs"
                  leftSection={<IconArrowLeft size={14} />}
                  w="fit-content"
                >
                  Back to variants
                </Button>

                <Stack align="center" gap="xs">
                  <Badge
                    leftSection={<IconPhoto size={12} />}
                    variant="gradient"
                    gradient={{ from: 'green', to: 'teal' }}
                    size="lg"
                  >
                    Illustrated Design
                  </Badge>
                  <Center hiddenFrom="md" style={{ marginTop: 'var(--mantine-spacing-md)' }}>
                    <AuthIllustration type="reset-password" size={120} animated />
                  </Center>
                  <Center
                    visibleFrom="md"
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 14,
                      backgroundColor: 'var(--mantine-color-green-light)',
                      marginTop: 'var(--mantine-spacing-md)',
                    }}
                  >
                    <IconLockCheck size={28} color="var(--mantine-color-green-6)" />
                  </Center>
                  <Title order={2} ta="center" mt="sm">
                    Set new password
                  </Title>
                  <Text c="dimmed" size="sm" ta="center" maw={300}>
                    Your new password must be different from previous passwords.
                  </Text>
                </Stack>

                <form onSubmit={handleSubmit}>
                  <Stack gap="md">
                    <div>
                      <PasswordInput
                        label="New password"
                        placeholder="Enter new password"
                        leftSection={<IconLock size={16} />}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        size="md"
                        radius="md"
                      />
                      {password && <PasswordStrength password={password} variant="bar" />}
                    </div>

                    <PasswordInput
                      label="Confirm password"
                      placeholder="Confirm new password"
                      leftSection={<IconLock size={16} />}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      size="md"
                      radius="md"
                      error={confirmPassword && !passwordsMatch ? "Passwords don't match" : undefined}
                    />

                    <Button
                      type="submit"
                      fullWidth
                      size="md"
                      radius="md"
                      variant="gradient"
                      gradient={{ from: 'green', to: 'teal' }}
                      disabled={!canSubmit}
                    >
                      Reset password
                    </Button>
                  </Stack>
                </form>

                <Center>
                  <Button
                    component={Link}
                    to="/app/demo/auth-pages/login/illustrated"
                    variant="subtle"
                    leftSection={<IconArrowLeft size={14} />}
                  >
                    Back to login
                  </Button>
                </Center>
              </Stack>
            )}
          </Container>
        </Center>
      </Box>
    </Box>
  );
}
